import { Directive, HostBinding, Input } from '@angular/core';
import { isNotEmpty, isNumber } from '@partners/utils';
import { FlexElementDirective } from './flex-element.directive';
import { TFlexChild, TFlexChildArgs, TFlexChildExpression } from './model';

@Directive({
  standalone: true,
  selector: '[flex]'
})
export class FlexChildDirective<
  Grow extends TFlexChild = 0,
  Shrink extends number = null,
  Basis extends number = null
> extends FlexElementDirective {
  @Input()
  set flex(value: TFlexChild | TFlexChildExpression<Grow, Shrink, Basis> | string) {
    if (isNumber(value)) {
      this.maxWidthPct = `${value}%`;
      this.flexChild = `1 1 100%` as string;
      return;
    }

    this.maxWidthPct = null;

    const flexArgs: TFlexChildArgs = this.matchFlexChildPattern(value as TFlexChildExpression<Grow, Shrink, Basis>);

    if (!isNumber(flexArgs.grow)) {
      this.flexChild = this.flexLiteralToCssRule(flexArgs.grow);
      return;
    }

    this.maxWidthPct = '100%';

    let flexExpression = `${flexArgs.grow}`;

    if (flexArgs.shrink) {
      flexExpression += ` ${flexArgs.shrink}`;
    }

    if (flexArgs.shrink && flexArgs.basis) {
      flexExpression += ` ${flexArgs.basis}`;
    }

    this.flexChild = flexExpression as TFlexChildExpression;
  }

  @HostBinding(`style.maxWidth`)
  maxWidthPct: string;

  @HostBinding(`style.flex`)
  flexChild: TFlexChild | TFlexChildExpression | string;

  constructor() {
    super();
  }

  flexLiteralToCssRule(literal: string | number): TFlexChildExpression {
    let flexExpression = '';

    switch (literal) {
      case 'initial': {
        flexExpression = `0 1 auto`;
        break;
      }
      case 'auto': {
        flexExpression = `1 1 auto`;
        break;
      }
      case 'grow':
        flexExpression = `1 1 100%`;
        break;
      case 'nogrow': {
        flexExpression = `0 1 auto`;
        break;
      }
      case 'noshrink': {
        flexExpression = `1 0 auto`;
        break;
      }
      default: {
        flexExpression = `0 0 auto`;
      }
    }
    return flexExpression as TFlexChildExpression;
  }

  private matchFlexChildPattern(input: TFlexChildExpression<Grow, Shrink, Basis>): TFlexChildArgs {
    const [grow, shrink, basis] = input.split(' ');

    const result: TFlexChildArgs = {
      basis: null,
      grow: null,
      shrink: null
    };

    if (grow as TFlexChild) {
      result.grow = grow as TFlexChild;
      /**
       * first args (grow) has dual purpose, it can have 1st arg for flex patten flex:X Y Z
       * or it can hav values such as 'grow' | 'shrink' | 'nogrow' | 'noshrink', the latter
       * case means we dont need other two (Y-shrink, Z-basis) args
       **/
      if (!isNumber(grow)) {
        return result;
      }
    }

    if (basis as TFlexChild) {
      isNotEmpty(basis) && (result.basis = basis as TFlexChild);
    }

    if (shrink as TFlexChild) {
      isNotEmpty(shrink) && (result.shrink = shrink as TFlexChild);
    }

    if (grow as TFlexChild) {
      isNotEmpty(basis) && (result.grow = grow as TFlexChild);
    }

    return result;
  }
}
